import AddIcon from "@mui/icons-material/Add";
import { Box } from "@mui/material";
import { FileField, RequestFile } from "models/interfaces";
import {
  setRadioGroupValue,
  setReduxSelectedFile,
  selectField,
  setSelectedField,
  updateFieldInSignRequest,
  updateFieldOfSelectedFile,
  updateFileInSignRequest,
} from "redux/appSlice";
import {
  fieldColor,
  fieldColorFor,
  getNextAvailableId,
} from "screens/SignRequest/SignRequestHelper";

const RadioField = ({
  field: fieldProps,
  dispatch,
  selectedFile,
  coords,
}: {
  field: FileField;
  dispatch: Function;
  selectedFile: RequestFile;
  coords: any;
  isMobile?: boolean;
}) => {
  const field: FileField =
    selectedFile.fields.find((f: FileField) => f.id === fieldProps.id) ??
    fieldProps;

  const isChecked = field.value === "true";

  const groupSiblings = selectedFile.fields.filter(
    (f: FileField) => f.type === "radio_button" && f.groupId === field.groupId,
  );
  const isLastInGroup =
    !!field.groupId &&
    groupSiblings.length > 0 &&
    groupSiblings[groupSiblings.length - 1].id === field.id;

  const color = fieldColorFor(field.signerIndex) ?? fieldColor[0];
  const minDim = Math.max(8, Math.min(coords.pageW, coords.pageH));
  const circleDiameter = minDim * 0.55;
  const innerDot = circleDiameter * 0.5;
  const addBtnSize = Math.max(16, Math.min(minDim * 0.9, 22));

  const handleCircleClick = (e: React.MouseEvent | React.TouchEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (!field.groupId) return;
    if (isChecked) {
      dispatch(updateFieldOfSelectedFile({ id: field.id, value: "false" }));
      dispatch(updateFieldInSignRequest({ id: field.id, value: "false" }));
      return;
    }
    dispatch(
      setRadioGroupValue({
        groupId: field.groupId,
        selectedFieldId: field.id,
      }),
    );
  };

  const addSibling = (e: React.MouseEvent | React.TouchEvent) => {
    e.stopPropagation();
    if (!field.groupId) return;
    const siblings = groupSiblings;
    const w = field.fieldPageWidth ?? field.w ?? 3;
    const h = field.fieldPageHeight ?? field.h ?? 3;

    let nextY = (field.fieldPageY ?? field.y ?? 0) + h + 0.5;
    let nextX = field.fieldPageX ?? field.x ?? 0;
    if (nextY + h > 99) {
      nextY = field.fieldPageY ?? field.y ?? 0;
      nextX = (field.fieldPageX ?? field.x ?? 0) + w + 0.5;
    }
    nextX = Math.min(Math.max(nextX, 0), Math.max(0, 100 - w));
    nextY = Math.min(Math.max(nextY, 0), Math.max(0, 100 - h));

    const newField: FileField = {
      ...(structuredClone(field) as FileField),
      id: getNextAvailableId(selectedFile),
      label: `Option ${siblings.length + 1}`,
      value: "false",
      x: nextX,
      y: nextY,
      fieldPageX: nextX,
      fieldPageY: nextY,
    };
    const updatedFile = {
      ...selectedFile,
      fields: [...selectedFile.fields, newField],
    };
    dispatch(setReduxSelectedFile(updatedFile));
    dispatch(updateFileInSignRequest(updatedFile));
    dispatch(selectField(newField));
  };

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxSizing: "border-box",
      }}
    >
      <Box
        onClick={handleCircleClick}
        onTouchEnd={handleCircleClick}
        onMouseDown={(e) => e.stopPropagation()}
        sx={{
          width: circleDiameter,
          height: circleDiameter,
          borderRadius: "50%",
          border: `1.5px solid ${color}`,
          backgroundColor: "transparent",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          flex: "0 0 auto",
        }}
      >
        {isChecked && (
          <Box
            sx={{
              width: innerDot,
              height: innerDot,
              borderRadius: "50%",
              backgroundColor: color,
            }}
          />
        )}
      </Box>
      {isLastInGroup && (
        <Box
          className="no-drag"
          onClick={addSibling}
          onTouchEnd={addSibling}
          onMouseDown={(e) => e.stopPropagation()}
          title="Add another option to this group"
          sx={{
            position: "absolute",
            top: "100%",
            left: "50%",
            marginTop: "4px",
            transform: "translateX(-50%)",
            width: addBtnSize,
            height: addBtnSize,
            borderRadius: "50%",
            backgroundColor: color,
            color: "#fff",
            border: "2px solid #fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            boxShadow: "0 2px 6px rgba(0, 0, 0, 0.35)",
            zIndex: 999,
            pointerEvents: "auto",
          }}
        >
          <AddIcon sx={{ fontSize: addBtnSize * 0.7 }} />
        </Box>
      )}
    </Box>
  );
};

export default RadioField;
